import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

// Picker chọn NHIỀU user — chip cho người đã chọn + search server (debounce 350ms).
// Popover render qua PORTAL (position:fixed) để không bị clip bởi card/cột cuộn.
// Props:
//   value: [{ id, email, name }]
//   onChange(nextArray)
//   fetcher(q): Promise<{ data: [{ value, email, label }] }>
//   placeholder
const initials = (s) => (String(s || "?").trim().slice(0, 2) || "?").toUpperCase();

const MultiUserPicker = ({ value = [], onChange, fetcher, placeholder = "Add people…" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];
  const has = (id, email) =>
    selected.some((u) => (id != null && u.id === id) || (email && u.email === email));

  // Search server (debounce) — chỉ chạy khi mở.
  useEffect(() => {
    if (!open || !fetcher) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetcher(q);
        setOptions(res?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, open, fetcher]);

  // Vị trí popover: bám mép dưới trigger, position:fixed.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const compute = () => {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    compute();
    const onMove = () => compute();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  // Đóng khi click ngoài; autofocus ô search khi mở.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!btnRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => { document.removeEventListener("mousedown", h); clearTimeout(t); };
  }, [open]);

  const add = (o) => {
    const id = o.value;
    const email = o.email;
    if (has(id, email)) return; // đã có -> bỏ qua
    const name = (o.label || "").split(" — ")[0];
    onChange([...selected, { id, email, name }]);
    setQ("");
  };

  const remove = (u) => onChange(selected.filter((x) => x.id !== u.id || x.email !== u.email));

  return (
    <>
      {/* Trigger: hàng chip + nút mở search */}
      <div
        ref={btnRef}
        className="tw-w-full tw-min-h-10 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-px-2 tw-py-1.5 tw-cursor-pointer hover:tw-border-geist-border-2 focus-within:tw-border-geist-accent tw-transition-colors"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
      >
        {selected.map((u) => (
          <span
            key={`${u.id}-${u.email}`}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-xl tw-bg-geist-100 tw-pl-1 tw-pr-1.5 tw-py-0.5 tw-text-[11px] tw-text-geist-fg"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-bg tw-text-geist-fg-2 tw-text-[8px] tw-font-semibold">
              {initials(u.name || u.email)}
            </span>
            <span className="tw-max-w-[160px] tw-truncate">{u.name || u.email}</span>
            <i
              role="button"
              title="Remove"
              className="bi bi-x tw-text-[12px] tw-text-geist-fg-3 hover:tw-text-geist-error"
              onClick={(e) => { e.stopPropagation(); remove(u); }}
            />
          </span>
        ))}
        <span className={`tw-flex-1 tw-min-w-[80px] tw-text-sm ${selected.length ? "tw-text-geist-fg-3 tw-text-xs" : "tw-text-geist-fg-3"}`}>
          {selected.length ? "Add more…" : placeholder}
        </span>
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[11px] tw-text-geist-fg-3 tw-shrink-0`} />
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, minWidth: 260, zIndex: 100000 }}
          className="geist-scope tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden"
        >
          <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
            <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg-2 tw-rounded-geist-sm tw-px-2.5 tw-py-1.5">
              <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
              <input
                ref={inputRef}
                autoComplete="off"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name / email..."
                className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
              />
            </div>
          </div>
          <div className="tw-max-h-60 tw-overflow-y-auto tw-py-1">
            {loading ? (
              <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-geist-fg-3 tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-arrow-repeat tw-animate-spin" /> Searching...
              </div>
            ) : options.length === 0 ? (
              <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-text-center">No results.</div>
            ) : (
              options.map((o) => {
                const name = (o.label || "").split(" — ")[0];
                const picked = has(o.value, o.email);
                return (
                  <div
                    key={o.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => add(o)}
                    className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors ${picked ? "tw-bg-geist-100 tw-opacity-60" : "hover:tw-bg-geist-100"}`}
                  >
                    <span className="tw-inline-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-text-[10px] tw-font-semibold">
                      {initials(name || o.email)}
                    </span>
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-geist-fg tw-truncate">{name || o.email}</span>
                      <span className="tw-block tw-text-[10px] tw-text-geist-fg-3 tw-truncate">{o.email}</span>
                    </span>
                    {picked && <i className="bi bi-check-lg tw-text-geist-accent tw-text-sm tw-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

MultiUserPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    name: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  fetcher: PropTypes.func,
  placeholder: PropTypes.string,
};

export default MultiUserPicker;
